import { useT } from "../../../shared/i18n/i18n";
import { skipToken, useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../../../shared/lib/cn";
import { useTRPC } from "../../../shared/api/trpc";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// Cmd+K: поиск по всем доступным доскам — по заголовку и по ключу задачи
// (MAIN-8). Стрелки и Enter открывают найденное прямо на его доске.
export function CommandSearch({
  onOpenChange,
  onOpenItem,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  onOpenItem: (target: { boardId: string; workObjectId: string }) => void;
  open: boolean;
}) {
  const t = useT();
  const trpcClient = useTRPC();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  const searchQuery = useQuery(
    trpcClient.workObjects.search.queryOptions(
      open && debouncedQuery.trim().length >= 2 ? { query: debouncedQuery } : skipToken,
      { placeholderData: (previous) => previous },
    ),
  );
  const results = debouncedQuery.trim().length >= 2 ? searchQuery.data ?? [] : [];
  const clampedIndex = Math.min(activeIndex, Math.max(results.length - 1, 0));

  function openResult(index: number) {
    const hit = results[index];
    if (!hit) return;
    onOpenItem({ boardId: hit.boardId, workObjectId: hit.workObjectId });
    onOpenChange(false);
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="top-[20%] max-w-xl translate-y-0 gap-0 p-0" showCloseButton={false}>
        <DialogTitle className="sr-only">{t("search.title")}</DialogTitle>
        <div className="flex items-center gap-2.5 px-4 py-3">
          {searchQuery.isFetching ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <Search className="size-4 shrink-0 text-muted-foreground" />
          )}
          <input
            autoFocus
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setActiveIndex((index) => Math.min(index + 1, results.length - 1));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setActiveIndex((index) => Math.max(index - 1, 0));
              } else if (event.key === "Enter") {
                event.preventDefault();
                openResult(clampedIndex);
              }
            }}
            placeholder={t("search.placeholder")}
            ref={inputRef}
            value={query}
          />
          <kbd className="shrink-0 rounded bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">esc</kbd>
        </div>

        {debouncedQuery.trim().length >= 2 ? (
          <div className="max-h-80 overflow-y-auto p-2">
            {results.length === 0 && !searchQuery.isFetching ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">{t("search.noMatches", { query: debouncedQuery })}</p>
            ) : null}
            {results.map((hit, index) => (
              <button
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors",
                  index === clampedIndex ? "bg-accent/60" : "hover:bg-accent/40",
                )}
                key={`${hit.boardId}:${hit.workObjectId}`}
                onClick={() => openResult(index)}
                onMouseMove={() => setActiveIndex(index)}
                type="button"
              >
                <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: hit.status.color }} />
                {hit.itemKey ? (
                  <span className="shrink-0 font-mono text-xs text-muted-foreground">{hit.itemKey}</span>
                ) : null}
                <span className="min-w-0 flex-1 truncate text-sm">{hit.workObjectTitle}</span>
                <span className="shrink-0 text-xs text-muted-foreground/70">{hit.boardName}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{hit.status.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="px-4 pb-4 text-xs text-muted-foreground/70">{t("search.typeTwo")}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
